import {ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {css, Theme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {calculateRemSize, flex, mb, onDesktop, p, w100} from "@pg-design/helpers-css";
import {EyeIcon, FiltersIcon, SortIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {toQueryValues} from "@pg-mono/form";
import {hitUserSegmentBudget, hitUserSegmentRooms} from "@pg-mono/google-tag-manager";
import {isEqual, omit} from "@pg-mono/nodash";
import {FILTERS_TEST_IDS, NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {DesktopBlend} from "../../../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../../../atoms/MobileOptionsModal";
import {useClickOutside} from "../../../../hooks/handle_click_outside";
import {trackFilterApply, trackFilterClear, trackFilterOpenForCategory, trackFilterRemove} from "../../../../offer/tracking/hit_offer_modal_filters";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues, PropertyListFilters} from "../../../../property_list/PropertyListFilters";
import {PropertyListTabSwitcher, PropertyListType, TabSwitcherStyledButton} from "../../../../property_list/PropertyListTabSwitcher";
import {useAppSelector} from "../../../../store/hooks";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {searchPropertyListHit} from "../../../../tracking/algolytics_hits/search_property_list_hit";
import {gtmEventPropertyListSearch} from "../../../../tracking/google_tag_manager/gtm_event_ui";
import {useHomeMatchPreferencesStorage} from "../../../../user/hooks/preferences/use_home_match_preferences_storage";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE, OFFER_DETAIL_PROPERTY_LIST_TILES_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {SortOptionsValues} from "../../types/SortOptionsValues";
import {Sort} from "../sort/Sort";
import {SortOptions} from "../sort/SortOptions";
import {initialPropertiesFilterValues} from "./OfferDetailPropertyList";
import {propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";

interface IProps {
    propertyListObj: IPropertyListObj;
    filterValues: ISearchFiltersFormValues;
    offerId: number;
    offer: IOfferDetail;
    setFilterValues: (filterValues: ISearchFiltersFormValues) => void;
    sortOptions: {label: string; value: number; slug: string}[];
    setCurrentPropertyListType: (type: PropertyListType) => void;
    currentPropertyListType: PropertyListType;
    /**
     * Optional promotion tabulator. It is rendered inline with the filters on desktop and below the mobile
     * filter/sort triggers on mobile.
     */
    tabsSlot?: ReactNode;
}
export const OfferDetailPropertyListFilters = (props: IProps) => {
    const {filterValues, setFilterValues, sortOptions, currentPropertyListType, setCurrentPropertyListType} = props;
    const {isMobile} = useUserDevice();

    const activeTab = useAppSelector((store) => store.offerDetail.properties.activeTab);

    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [isDesktopFilterBlendActive, setDesktopFilterBlendActive] = useState(false);
    const [isDesktopSortBlendActive, setDesktopSortBlendActive] = useState(false);
    const [isMobileFiltersActive, setMobileFiltersActive] = useState(false);
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    // TODO: we should use sort from `propertyListObj` instead of relying on local state
    const [sortValue, setSortValue] = useState(0);

    const latestFetchValues = useRef<ISearchFiltersFormValues>(filterValues);
    const sortRef = useRef<HTMLElement | null>(null);
    const prevFilterState = useRef<ISearchFiltersFormValues | null>(null);

    const offerType = props.offer.type;

    const queryValues = {sort: props.propertyListObj.sort.currentSort, page: 1};
    const dispatch = useDispatch();

    const sortRefHandle = (element: HTMLElement | null) => {
        sortRef.current = element;
    };

    const {putPreferences} = useHomeMatchPreferencesStorage();

    useClickOutside(sortRef, () => {
        setDropdownOpen(false);
        setDesktopFilterBlendActive(false);
        setDesktopSortBlendActive(false);
    });

    /*
        mobile modals handling
    */
    const toggleMobileFilterModal = () => {
        setMobileFiltersActive((prev) => {
            const isOpening = !prev;

            if (isOpening && isMobile) {
                trackFilterOpenForCategory({filterCategory: "filters", eventName: "invest_filters"});
            }

            prevFilterState.current = prev
                ? null
                : {
                      rooms: filterValues.rooms,
                      price: filterValues.price,
                      area: filterValues.area,
                      floor_choices: filterValues.floor_choices,
                      house_storeys: filterValues.house_storeys
                  };
            return !prev;
        });
    };

    const toggleMobileSortModal = () => {
        setMobileSortActive((prev) => {
            const isOpening = !prev;

            if (isOpening && isMobile) {
                trackFilterOpenForCategory({filterCategory: "Sortuj", eventName: "invest_filters"});
            }

            return isOpening;
        });
    };

    const discardModal = () => {
        closeMobileModal();
        if (prevFilterState.current != null) {
            setFilterValues(prevFilterState.current);
        }
        if (sortValue !== 0) {
            setSortValue(sortValue);
        }
    };

    const closeMobileModal = () => {
        if (isMobileFiltersActive) {
            setMobileFiltersActive(false);
        } else {
            setMobileSortActive(false);
        }
    };

    /*
        filters handling logic
    */
    const fetchFilteredProperties = async <T extends keyof ISearchFiltersFormValues>(name?: T, value?: ISearchFiltersFormValues[T]) => {
        //check for changes values in old redux form
        const filtersHasChange = !isEqual(latestFetchValues.current, filterValues);

        if (name && value && isEqual(latestFetchValues.current[name], value) && !filtersHasChange) {
            // prevent desktop from fetching data on every click that opens single field
            return;
        }
        const newFilters = {...filterValues};

        if (name && value && filterValues[name] !== undefined) {
            newFilters[name] = value;
        }

        const hasRoomsChanged = !isEqual(latestFetchValues.current.rooms, newFilters.rooms);
        const hasAreaChanged = !isEqual(latestFetchValues.current.area, newFilters.area);
        const hasPriceChanged = !isEqual(latestFetchValues.current.price, newFilters.price);

        if (hasRoomsChanged && props.offer.region.country === Country.POLAND) {
            putPreferences({rooms: newFilters.rooms});
        }

        hasRoomsChanged && gtmEventPropertyListSearch("pokoje");
        hasAreaChanged && gtmEventPropertyListSearch("powierzchnia");
        hasPriceChanged && gtmEventPropertyListSearch("cena");

        if (hasRoomsChanged) hitUserSegmentRooms(newFilters.rooms ?? {});
        if (hasPriceChanged) hitUserSegmentBudget({priceUpper: newFilters.price?.upper, instalment: ""});

        const filterQueryValues = toQueryValues(propertyListTableFiltersFields, newFilters);

        // keep the user on the current tab when filtering - the promotion tab automatically appends is_promo=true
        const tabToFetch = activeTab === PropertyListTabTypes.PROMOTION ? PropertyListTabTypes.PROMOTION : PropertyListTabTypes.FILTER;

        if (props.offerId) {
            latestFetchValues.current = newFilters;
            const properties = await fetchOfferDetailProperties(
                props.offerId,
                tabToFetch,
                OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                queryValues,
                filterQueryValues
            )(dispatch);

            dispatch(fetchOfferDetailLowestPriceProperty(props.offerId, filterQueryValues));

            if (properties) {
                searchPropertyListHit(
                    omit(newFilters, ["floor_choices", "house_storeys"]),
                    properties.map((el) => el.id),
                    props.offerId
                );
            }
        }

        if (props.propertyListObj.tab.currentTab !== tabToFetch) {
            props.propertyListObj.tab.setCurrentTab(tabToFetch);
        }
    };

    const updateFiltersOnChange = (filter: Partial<ISearchFiltersFormValues>) => {
        const newFilters = {...filterValues, ...filter};
        setFilterValues(newFilters);
    };

    const resetFilters = () => {
        setFilterValues(initialPropertiesFilterValues);
        // resetting filters keeps the user on the current tab (the promotion tab still appends is_promo=true)
        const tabToFetch = activeTab === PropertyListTabTypes.PROMOTION ? PropertyListTabTypes.PROMOTION : PropertyListTabTypes.FILTER;
        if (props.offerId) {
            fetchOfferDetailProperties(props.offerId, tabToFetch, OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE, {
                sort: props.propertyListObj.sort.currentSort,
                page: 1
            })(dispatch);

            dispatch(fetchOfferDetailLowestPriceProperty(props.offerId));
        }
        props.propertyListObj.tab.setCurrentTab(tabToFetch);

        closeMobileModal();
    };

    /*
        sort handling logic
    */

    const clearSort = () => {
        const previousValue = sortValue;

        setSortValue(0);

        if (isMobile) {
            setMobileSortActive(false);
        }

        if (previousValue !== 0) {
            trackFilterClear({
                eventName: "invest_filters",
                filterType: "sort",
                value: previousValue,
                sortOptions
            });
        }

        props.propertyListObj.onSortChange(null);

        setDropdownOpen(false);
        setDesktopSortBlendActive(false);
    };

    const saveSort = () => {
        // Don't apply filters for INVESTMENT tab (investment properties with ROI don't support filtering)
        const query = activeTab === PropertyListTabTypes.INVESTMENT ? {} : toQueryValues(propertyListTableFiltersFields, filterValues);

        if (sortValue === SortOptionsValues.NOT_SELECTED) {
            props.propertyListObj.onSortChange(null, query);
        } else {
            const sort = sortOptions.find((el) => el.value === sortValue);

            props.propertyListObj.onSortChange(sort?.slug ?? null, query);

            trackFilterApply({
                eventName: "invest_filters",
                filterType: "sort",
                value: sortValue,
                sortOptions
            });
        }

        if (isMobile) {
            setMobileSortActive(false);
        }

        setDropdownOpen(false);
        setDesktopSortBlendActive(false);
    };

    /*
        tab changing logic
    */
    const switchCurrentPropertyListType = async (type: PropertyListType) => {
        if (type !== currentPropertyListType) {
            const isTableTab = type === PropertyListType.TABLE;

            // Don't apply filters for INVESTMENT tab (investment properties with ROI don't support filtering)
            const filtersToApply = activeTab === PropertyListTabTypes.INVESTMENT ? {} : toQueryValues(propertyListTableFiltersFields, filterValues);

            await fetchOfferDetailProperties(
                props.offerId,
                activeTab,
                isTableTab ? OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE : OFFER_DETAIL_PROPERTY_LIST_TILES_DEFAULT_PAGE_SIZE,
                queryValues,
                filtersToApply
            )(dispatch);
            setCurrentPropertyListType(type);
            type === PropertyListType.TILES && showPropertyPlanView(ShowPropertyPlanView.PLAN_DEFAULT, props.offer, props.offer.vendor);
            saveSort();
        }
    };

    /*
        UseEffect below is responsible for sorting using the header from the PropertyListTable.tsx
     */
    useEffect(() => {
        const slug = props.propertyListObj.sort.currentSort;
        const currentSort = sortOptions.find((el) => el.value === sortValue);
        if ((!!currentSort && currentSort.slug !== slug) || sortValue === SortOptionsValues.NOT_SELECTED) {
            const newSort = sortOptions.find((el) => el.slug === slug);
            setSortValue(!!newSort ? newSort.value : sortValue);
        }
    }, [props.propertyListObj.sort.currentSort]);

    const activeFiltersCount = useMemo(() => {
        const hasRangeValue = (range: {lower: string | number; upper: string | number}) => range.lower !== "" || range.upper !== "";

        const hasFloorsValue = filterValues.floor_choices.length > 0 || filterValues.house_storeys.length > 0;

        return (
            Number(hasRangeValue(filterValues.price)) +
            Number(hasRangeValue(filterValues.rooms)) +
            Number(hasRangeValue(filterValues.area)) +
            Number(hasFloorsValue)
        );
    }, [filterValues]);

    return (
        <>
            <DesktopBlend isActive={isDesktopFilterBlendActive || isDesktopSortBlendActive} isFullScreen />

            <div ref={sortRefHandle} css={wrapper}>
                {activeTab !== PropertyListTabTypes.INVESTMENT && (
                    <div css={desktopWrapper}>
                        {props.tabsSlot && <div css={desktopTabsInnerStyle}>{props.tabsSlot}</div>}

                        <PropertyListFilters
                            values={filterValues}
                            availableRooms={props.offer.stats?.rooms}
                            onChange={updateFiltersOnChange}
                            onAfterChange={fetchFilteredProperties}
                            onMobileCloseModal={closeMobileModal}
                            onMobileResetForm={resetFilters}
                            setDesktopFilterBlendActive={setDesktopFilterBlendActive}
                            offerType={offerType}
                            css={desktopFilters}
                            currency={props.offer.currency}
                            maxPriceInOffer={props.offer.stats?.ranges_price_max}
                        />

                        <InputWrapper css={sortFieldStyle}>
                            <Sort
                                isDropdownOpen={isDropdownOpen}
                                setDropdownOpen={setDropdownOpen}
                                isMobile={isMobile as boolean}
                                setDesktopFilterBlendActive={setDesktopSortBlendActive}
                                currentValue={sortValue}
                                setCurrentValue={setSortValue}
                                sortOptions={sortOptions}
                                onOpen={() => {
                                    trackFilterOpenForCategory({filterCategory: "Sortuj", eventName: "invest_filters"});
                                }}
                                onClear={clearSort}
                                onRemove={() => {
                                    if (sortValue !== 0) {
                                        trackFilterRemove({
                                            eventName: "invest_filters",
                                            filterType: "sort",
                                            value: sortValue,
                                            sortOptions
                                        });
                                    }
                                }}
                            >
                                <SortOptions
                                    currentValue={sortValue}
                                    setCurrentValue={setSortValue}
                                    sortOptions={sortOptions}
                                    onCancel={clearSort}
                                    onSave={saveSort}
                                />
                            </Sort>
                        </InputWrapper>
                    </div>
                )}

                <div css={wrapperStyles}>
                    <div css={listHeaderStyle}>
                        <PropertyListTabSwitcher
                            currentPropertyListType={currentPropertyListType}
                            setCurrentPropertyListType={switchCurrentPropertyListType}
                            buttonCss={tabSwitcherButtonStyle}
                            css={tabSwitcherStyle}
                        />
                        <Planner3dModal offer={props.offer} css={filtersPlanner3D} routedModalId="propertyList">
                            <TabSwitcherStyledButton variant="none_secondary" size="medium" iconLeft={EyeIcon} isActive>
                                3D
                            </TabSwitcherStyledButton>
                        </Planner3dModal>
                        {activeTab !== PropertyListTabTypes.INVESTMENT && isMobile && (
                            <>
                                <div css={filtersIconWrapper} onClick={toggleMobileFilterModal}>
                                    <FiltersIcon
                                        size="2.4"
                                        wrapperSize="2.4"
                                        wrapperColor="transparent"
                                        css={{marginLeft: "auto"}}
                                        dataTestId={FILTERS_TEST_IDS.FILTER_MOBILE_ICON}
                                    />

                                    {activeFiltersCount > 0 && (
                                        <Badge variant="label_danger" css={filtersBadgeStyle}>
                                            {activeFiltersCount}
                                        </Badge>
                                    )}
                                </div>

                                <MobileOptionsModal modalHeading="Filtry" isOpen={isMobileFiltersActive} onModalClose={discardModal}>
                                    <PropertyListFilters
                                        noWrapper
                                        values={filterValues}
                                        onChange={updateFiltersOnChange}
                                        onAfterChange={fetchFilteredProperties}
                                        onMobileCloseModal={closeMobileModal}
                                        onMobileResetForm={resetFilters}
                                        setDesktopFilterBlendActive={setDesktopFilterBlendActive}
                                        currency={props.offer.currency}
                                        maxPriceInOffer={props.offer.stats?.ranges_price_max}
                                        offerType={offerType}
                                    />
                                </MobileOptionsModal>
                            </>
                        )}
                        {isMobile && (
                            <>
                                <SortIcon
                                    onClick={toggleMobileSortModal}
                                    size="2.4"
                                    wrapperSize="2.4"
                                    wrapperColor="transparent"
                                    dataTestId={NAVIGATION_TEST_IDS.SORT_ICON}
                                />

                                <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={discardModal}>
                                    <SortOptions
                                        currentValue={sortValue}
                                        setCurrentValue={setSortValue}
                                        sortOptions={sortOptions}
                                        onCancel={clearSort}
                                        onSave={saveSort}
                                    />
                                </MobileOptionsModal>
                            </>
                        )}
                    </div>

                    {props.tabsSlot && <div css={mobileTabsStyle}>{props.tabsSlot}</div>}
                </div>
            </div>
        </>
    );
};

const desktopWrapper = (props: Theme) => css`
    justify-items: stretch;
    display: none;

    ${mb(3)};

    @media (min-width: ${props.breakpoints.md}) {
        display: flex;
    }

    @media (min-width: ${props.breakpoints.lg}) {
        width: 100%;
        ${mb(0)};
    }
`;

// promotion tabulator placed inline with the desktop filters; the gap to the filters grows dynamically
const desktopTabsInnerStyle = css`
    flex: 0 0 auto;
    align-self: center;
    margin-right: auto;
`;

// promotion tabulator placed below the mobile filter/sort triggers
const mobileTabsStyle = (theme: Theme) => css`
    display: block;
    overflow-x: auto;
    margin: ${calculateRemSize(2)} 0;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const wrapperStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;

    ${onDesktop(css`
        flex-direction: row;
    `)}
`;

export const sortWrapper = (theme: Theme) => css`
    ${p(0)};

    ${onDesktop(css`
        flex: 0 0 20%;
    `)};

    @media (min-width: ${theme.breakpoints.lg}) {
        min-width: 200px;
    }
`;

const sortFieldStyle = css`
    ${p(0)};

    ${onDesktop(css`
        flex: 0 0 auto;
    `)};
`;

const wrapper = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.lg}) {
        ${flex("center")};
        gap: ${calculateRemSize(4)};
    }
`;

const desktopFilters = css`
    min-width: 0;
`;

const listHeaderStyle = (theme: Theme) => css`
    ${flex("center", "space-between")};
    gap: ${calculateRemSize(3)};
    ${w100};

    @media (min-width: ${theme.breakpoints.md}) {
        width: initial;
        flex-basis: initial;
        gap: 0;
    }
`;

const tabSwitcherButtonStyle = (theme: Theme) => css`
    ${p(2, 0.5)};
    height: initial;

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(2)};
    }
`;

const tabSwitcherStyle = css`
    gap: ${calculateRemSize(2)};
`;

const filtersPlanner3D = (theme: Theme) => css`
    display: none;
    @media (min-width: ${theme.breakpoints.md}) {
        display: inline-flex;
    }
`;

const filtersIconWrapper = css`
    margin-left: auto;
    position: relative;
    display: inline-flex;
`;

const filtersBadgeStyle = css`
    position: absolute;
    top: -${calculateRemSize(0.5)};
    right: -${calculateRemSize(0.5)};
    padding: 0.25rem 0.5rem;
    min-height: ${calculateRemSize(2)};
    font-size: ${calculateRemSize(2)};
    line-height: 1;
`;
import {ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {css, Theme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {calculateRemSize, flex, mb, onDesktop, p, w100} from "@pg-design/helpers-css";
import {EyeIcon, FiltersIcon, SortIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {toQueryValues} from "@pg-mono/form";
import {hitUserSegmentBudget, hitUserSegmentRooms} from "@pg-mono/google-tag-manager";
import {isEqual, omit} from "@pg-mono/nodash";
import {FILTERS_TEST_IDS, NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {DesktopBlend} from "../../../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../../../atoms/MobileOptionsModal";
import {useClickOutside} from "../../../../hooks/handle_click_outside";
import {trackFilterApply, trackFilterClear, trackFilterOpenForCategory, trackFilterRemove} from "../../../../offer/tracking/hit_offer_modal_filters";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues, PropertyListFilters} from "../../../../property_list/PropertyListFilters";
import {PropertyListTabSwitcher, PropertyListType, TabSwitcherStyledButton} from "../../../../property_list/PropertyListTabSwitcher";
import {useAppSelector} from "../../../../store/hooks";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {searchPropertyListHit} from "../../../../tracking/algolytics_hits/search_property_list_hit";
import {gtmEventPropertyListSearch} from "../../../../tracking/google_tag_manager/gtm_event_ui";
import {useHomeMatchPreferencesStorage} from "../../../../user/hooks/preferences/use_home_match_preferences_storage";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE, OFFER_DETAIL_PROPERTY_LIST_TILES_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {SortOptionsValues} from "../../types/SortOptionsValues";
import {Sort} from "../sort/Sort";
import {SortOptions} from "../sort/SortOptions";
import {initialPropertiesFilterValues} from "./OfferDetailPropertyList";
import {propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";

interface IProps {
    propertyListObj: IPropertyListObj;
    filterValues: ISearchFiltersFormValues;
    offerId: number;
    offer: IOfferDetail;
    setFilterValues: (filterValues: ISearchFiltersFormValues) => void;
    sortOptions: {label: string; value: number; slug: string}[];
    setCurrentPropertyListType: (type: PropertyListType) => void;
    currentPropertyListType: PropertyListType;
    /**
     * Optional promotion tabulator. It is rendered inline with the filters on desktop and below the mobile
     * filter/sort triggers on mobile.
     */
    tabsSlot?: ReactNode;
}
export const OfferDetailPropertyListFilters = (props: IProps) => {
    const {filterValues, setFilterValues, sortOptions, currentPropertyListType, setCurrentPropertyListType} = props;
    const {isMobile} = useUserDevice();

    const activeTab = useAppSelector((store) => store.offerDetail.properties.activeTab);

    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [isDesktopFilterBlendActive, setDesktopFilterBlendActive] = useState(false);
    const [isDesktopSortBlendActive, setDesktopSortBlendActive] = useState(false);
    const [isMobileFiltersActive, setMobileFiltersActive] = useState(false);
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    // TODO: we should use sort from `propertyListObj` instead of relying on local state
    const [sortValue, setSortValue] = useState(0);

    const latestFetchValues = useRef<ISearchFiltersFormValues>(filterValues);
    const sortRef = useRef<HTMLElement | null>(null);
    const prevFilterState = useRef<ISearchFiltersFormValues | null>(null);

    const offerType = props.offer.type;

    const queryValues = {sort: props.propertyListObj.sort.currentSort, page: 1};
    const dispatch = useDispatch();

    const sortRefHandle = (element: HTMLElement | null) => {
        sortRef.current = element;
    };

    const {putPreferences} = useHomeMatchPreferencesStorage();

    useClickOutside(sortRef, () => {
        setDropdownOpen(false);
        setDesktopFilterBlendActive(false);
        setDesktopSortBlendActive(false);
    });

    /*
        mobile modals handling
    */
    const toggleMobileFilterModal = () => {
        setMobileFiltersActive((prev) => {
            const isOpening = !prev;

            if (isOpening && isMobile) {
                trackFilterOpenForCategory({filterCategory: "filters", eventName: "invest_filters"});
            }

            prevFilterState.current = prev
                ? null
                : {
                      rooms: filterValues.rooms,
                      price: filterValues.price,
                      area: filterValues.area,
                      floor_choices: filterValues.floor_choices,
                      house_storeys: filterValues.house_storeys
                  };
            return !prev;
        });
    };

    const toggleMobileSortModal = () => {
        setMobileSortActive((prev) => {
            const isOpening = !prev;

            if (isOpening && isMobile) {
                trackFilterOpenForCategory({filterCategory: "Sortuj", eventName: "invest_filters"});
            }

            return isOpening;
        });
    };

    const discardModal = () => {
        closeMobileModal();
        if (prevFilterState.current != null) {
            setFilterValues(prevFilterState.current);
        }
        if (sortValue !== 0) {
            setSortValue(sortValue);
        }
    };

    const closeMobileModal = () => {
        if (isMobileFiltersActive) {
            setMobileFiltersActive(false);
        } else {
            setMobileSortActive(false);
        }
    };

    /*
        filters handling logic
    */
    const fetchFilteredProperties = async <T extends keyof ISearchFiltersFormValues>(name?: T, value?: ISearchFiltersFormValues[T]) => {
        //check for changes values in old redux form
        const filtersHasChange = !isEqual(latestFetchValues.current, filterValues);

        if (name && value && isEqual(latestFetchValues.current[name], value) && !filtersHasChange) {
            // prevent desktop from fetching data on every click that opens single field
            return;
        }
        const newFilters = {...filterValues};

        if (name && value && filterValues[name] !== undefined) {
            newFilters[name] = value;
        }

        const hasRoomsChanged = !isEqual(latestFetchValues.current.rooms, newFilters.rooms);
        const hasAreaChanged = !isEqual(latestFetchValues.current.area, newFilters.area);
        const hasPriceChanged = !isEqual(latestFetchValues.current.price, newFilters.price);

        if (hasRoomsChanged && props.offer.region.country === Country.POLAND) {
            putPreferences({rooms: newFilters.rooms});
        }

        hasRoomsChanged && gtmEventPropertyListSearch("pokoje");
        hasAreaChanged && gtmEventPropertyListSearch("powierzchnia");
        hasPriceChanged && gtmEventPropertyListSearch("cena");

        if (hasRoomsChanged) hitUserSegmentRooms(newFilters.rooms ?? {});
        if (hasPriceChanged) hitUserSegmentBudget({priceUpper: newFilters.price?.upper, instalment: ""});

        const filterQueryValues = toQueryValues(propertyListTableFiltersFields, newFilters);

        // keep the user on the current tab when filtering - the promotion tab automatically appends is_promo=true
        const tabToFetch = activeTab === PropertyListTabTypes.PROMOTION ? PropertyListTabTypes.PROMOTION : PropertyListTabTypes.FILTER;

        if (props.offerId) {
            latestFetchValues.current = newFilters;
            const properties = await fetchOfferDetailProperties(
                props.offerId,
                tabToFetch,
                OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                queryValues,
                filterQueryValues
            )(dispatch);

            dispatch(fetchOfferDetailLowestPriceProperty(props.offerId, filterQueryValues));

            if (properties) {
                searchPropertyListHit(
                    omit(newFilters, ["floor_choices", "house_storeys"]),
                    properties.map((el) => el.id),
                    props.offerId
                );
            }
        }

        if (props.propertyListObj.tab.currentTab !== tabToFetch) {
            props.propertyListObj.tab.setCurrentTab(tabToFetch);
        }
    };

    const updateFiltersOnChange = (filter: Partial<ISearchFiltersFormValues>) => {
        const newFilters = {...filterValues, ...filter};
        setFilterValues(newFilters);
    };

    const resetFilters = () => {
        setFilterValues(initialPropertiesFilterValues);
        // resetting filters keeps the user on the current tab (the promotion tab still appends is_promo=true)
        const tabToFetch = activeTab === PropertyListTabTypes.PROMOTION ? PropertyListTabTypes.PROMOTION : PropertyListTabTypes.FILTER;
        if (props.offerId) {
            fetchOfferDetailProperties(props.offerId, tabToFetch, OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE, {
                sort: props.propertyListObj.sort.currentSort,
                page: 1
            })(dispatch);

            dispatch(fetchOfferDetailLowestPriceProperty(props.offerId));
        }
        props.propertyListObj.tab.setCurrentTab(tabToFetch);

        closeMobileModal();
    };

    /*
        sort handling logic
    */

    const clearSort = () => {
        const previousValue = sortValue;

        setSortValue(0);

        if (isMobile) {
            setMobileSortActive(false);
        }

        if (previousValue !== 0) {
            trackFilterClear({
                eventName: "invest_filters",
                filterType: "sort",
                value: previousValue,
                sortOptions
            });
        }

        props.propertyListObj.onSortChange(null);

        setDropdownOpen(false);
        setDesktopSortBlendActive(false);
    };

    const saveSort = () => {
        // Don't apply filters for INVESTMENT tab (investment properties with ROI don't support filtering)
        const query = activeTab === PropertyListTabTypes.INVESTMENT ? {} : toQueryValues(propertyListTableFiltersFields, filterValues);

        if (sortValue === SortOptionsValues.NOT_SELECTED) {
            props.propertyListObj.onSortChange(null, query);
        } else {
            const sort = sortOptions.find((el) => el.value === sortValue);

            props.propertyListObj.onSortChange(sort?.slug ?? null, query);

            trackFilterApply({
                eventName: "invest_filters",
                filterType: "sort",
                value: sortValue,
                sortOptions
            });
        }

        if (isMobile) {
            setMobileSortActive(false);
        }

        setDropdownOpen(false);
        setDesktopSortBlendActive(false);
    };

    /*
        tab changing logic
    */
    const switchCurrentPropertyListType = async (type: PropertyListType) => {
        if (type !== currentPropertyListType) {
            const isTableTab = type === PropertyListType.TABLE;

            // Don't apply filters for INVESTMENT tab (investment properties with ROI don't support filtering)
            const filtersToApply = activeTab === PropertyListTabTypes.INVESTMENT ? {} : toQueryValues(propertyListTableFiltersFields, filterValues);

            await fetchOfferDetailProperties(
                props.offerId,
                activeTab,
                isTableTab ? OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE : OFFER_DETAIL_PROPERTY_LIST_TILES_DEFAULT_PAGE_SIZE,
                queryValues,
                filtersToApply
            )(dispatch);
            setCurrentPropertyListType(type);
            type === PropertyListType.TILES && showPropertyPlanView(ShowPropertyPlanView.PLAN_DEFAULT, props.offer, props.offer.vendor);
            saveSort();
        }
    };

    /*
        UseEffect below is responsible for sorting using the header from the PropertyListTable.tsx
     */
    useEffect(() => {
        const slug = props.propertyListObj.sort.currentSort;
        const currentSort = sortOptions.find((el) => el.value === sortValue);
        if ((!!currentSort && currentSort.slug !== slug) || sortValue === SortOptionsValues.NOT_SELECTED) {
            const newSort = sortOptions.find((el) => el.slug === slug);
            setSortValue(!!newSort ? newSort.value : sortValue);
        }
    }, [props.propertyListObj.sort.currentSort]);

    const activeFiltersCount = useMemo(() => {
        const hasRangeValue = (range: {lower: string | number; upper: string | number}) => range.lower !== "" || range.upper !== "";

        const hasFloorsValue = filterValues.floor_choices.length > 0 || filterValues.house_storeys.length > 0;

        return (
            Number(hasRangeValue(filterValues.price)) +
            Number(hasRangeValue(filterValues.rooms)) +
            Number(hasRangeValue(filterValues.area)) +
            Number(hasFloorsValue)
        );
    }, [filterValues]);

    return (
        <>
            <DesktopBlend isActive={isDesktopFilterBlendActive || isDesktopSortBlendActive} isFullScreen />

            <div ref={sortRefHandle} css={wrapper}>
                {activeTab !== PropertyListTabTypes.INVESTMENT && (
                    <div css={desktopWrapper}>
                        {props.tabsSlot && <div css={desktopTabsInnerStyle}>{props.tabsSlot}</div>}

                        <PropertyListFilters
                            values={filterValues}
                            availableRooms={props.offer.stats?.rooms}
                            onChange={updateFiltersOnChange}
                            onAfterChange={fetchFilteredProperties}
                            onMobileCloseModal={closeMobileModal}
                            onMobileResetForm={resetFilters}
                            setDesktopFilterBlendActive={setDesktopFilterBlendActive}
                            offerType={offerType}
                            css={desktopFilters}
                            currency={props.offer.currency}
                            maxPriceInOffer={props.offer.stats?.ranges_price_max}
                        />

                        <InputWrapper css={sortFieldStyle}>
                            <Sort
                                isDropdownOpen={isDropdownOpen}
                                setDropdownOpen={setDropdownOpen}
                                isMobile={isMobile as boolean}
                                setDesktopFilterBlendActive={setDesktopSortBlendActive}
                                currentValue={sortValue}
                                setCurrentValue={setSortValue}
                                sortOptions={sortOptions}
                                onOpen={() => {
                                    trackFilterOpenForCategory({filterCategory: "Sortuj", eventName: "invest_filters"});
                                }}
                                onClear={clearSort}
                                onRemove={() => {
                                    if (sortValue !== 0) {
                                        trackFilterRemove({
                                            eventName: "invest_filters",
                                            filterType: "sort",
                                            value: sortValue,
                                            sortOptions
                                        });
                                    }
                                }}
                            >
                                <SortOptions
                                    currentValue={sortValue}
                                    setCurrentValue={setSortValue}
                                    sortOptions={sortOptions}
                                    onCancel={clearSort}
                                    onSave={saveSort}
                                />
                            </Sort>
                        </InputWrapper>
                    </div>
                )}

                <div css={wrapperStyles}>
                    <div css={listHeaderStyle}>
                        <PropertyListTabSwitcher
                            currentPropertyListType={currentPropertyListType}
                            setCurrentPropertyListType={switchCurrentPropertyListType}
                            buttonCss={tabSwitcherButtonStyle}
                            css={tabSwitcherStyle}
                        />
                        <Planner3dModal offer={props.offer} css={filtersPlanner3D} routedModalId="propertyList">
                            <TabSwitcherStyledButton variant="none_secondary" size="medium" iconLeft={EyeIcon} isActive>
                                3D
                            </TabSwitcherStyledButton>
                        </Planner3dModal>
                        {activeTab !== PropertyListTabTypes.INVESTMENT && isMobile && (
                            <>
                                <div css={filtersIconWrapper} onClick={toggleMobileFilterModal}>
                                    <FiltersIcon
                                        size="2.4"
                                        wrapperSize="2.4"
                                        wrapperColor="transparent"
                                        css={{marginLeft: "auto"}}
                                        dataTestId={FILTERS_TEST_IDS.FILTER_MOBILE_ICON}
                                    />

                                    {activeFiltersCount > 0 && (
                                        <Badge variant="label_danger" css={filtersBadgeStyle}>
                                            {activeFiltersCount}
                                        </Badge>
                                    )}
                                </div>

                                <MobileOptionsModal modalHeading="Filtry" isOpen={isMobileFiltersActive} onModalClose={discardModal}>
                                    <PropertyListFilters
                                        noWrapper
                                        values={filterValues}
                                        onChange={updateFiltersOnChange}
                                        onAfterChange={fetchFilteredProperties}
                                        onMobileCloseModal={closeMobileModal}
                                        onMobileResetForm={resetFilters}
                                        setDesktopFilterBlendActive={setDesktopFilterBlendActive}
                                        currency={props.offer.currency}
                                        maxPriceInOffer={props.offer.stats?.ranges_price_max}
                                        offerType={offerType}
                                    />
                                </MobileOptionsModal>
                            </>
                        )}
                        {isMobile && (
                            <>
                                <SortIcon
                                    onClick={toggleMobileSortModal}
                                    size="2.4"
                                    wrapperSize="2.4"
                                    wrapperColor="transparent"
                                    dataTestId={NAVIGATION_TEST_IDS.SORT_ICON}
                                />

                                <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={discardModal}>
                                    <SortOptions
                                        currentValue={sortValue}
                                        setCurrentValue={setSortValue}
                                        sortOptions={sortOptions}
                                        onCancel={clearSort}
                                        onSave={saveSort}
                                    />
                                </MobileOptionsModal>
                            </>
                        )}
                    </div>

                    {props.tabsSlot && <div css={mobileTabsStyle}>{props.tabsSlot}</div>}
                </div>
            </div>
        </>
    );
};

const desktopWrapper = (props: Theme) => css`
    justify-items: stretch;
    display: none;

    ${mb(3)};

    @media (min-width: ${props.breakpoints.md}) {
        display: flex;
    }

    @media (min-width: ${props.breakpoints.lg}) {
        width: 100%;
        ${mb(0)};
    }
`;

// promotion tabulator placed inline with the desktop filters; the gap to the filters grows dynamically
const desktopTabsInnerStyle = css`
    flex: 0 0 auto;
    align-self: center;
    margin-right: auto;
`;

// promotion tabulator placed below the mobile filter/sort triggers
const mobileTabsStyle = (theme: Theme) => css`
    display: block;
    overflow-x: auto;
    margin: ${calculateRemSize(2)} 0;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const wrapperStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;

    ${onDesktop(css`
        flex-direction: row;
    `)}
`;

export const sortWrapper = (theme: Theme) => css`
    ${p(0)};

    ${onDesktop(css`
        flex: 0 0 20%;
    `)};

    @media (min-width: ${theme.breakpoints.lg}) {
        min-width: 200px;
    }
`;

const sortFieldStyle = css`
    ${p(0)};

    ${onDesktop(css`
        flex: 0 0 auto;
    `)};
`;

const wrapper = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.lg}) {
        ${flex("center")};
        gap: ${calculateRemSize(4)};
    }
`;

const desktopFilters = css`
    min-width: 0;
`;

const listHeaderStyle = (theme: Theme) => css`
    ${flex("center", "space-between")};
    gap: ${calculateRemSize(3)};
    ${w100};

    @media (min-width: ${theme.breakpoints.md}) {
        width: initial;
        flex-basis: initial;
        gap: 0;
    }
`;

const tabSwitcherButtonStyle = (theme: Theme) => css`
    ${p(2, 0.5)};
    height: initial;

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(2)};
    }
`;

const tabSwitcherStyle = css`
    gap: ${calculateRemSize(2)};
`;

const filtersPlanner3D = (theme: Theme) => css`
    display: none;
    @media (min-width: ${theme.breakpoints.md}) {
        display: inline-flex;
    }
`;

const filtersIconWrapper = css`
    margin-left: auto;
    position: relative;
    display: inline-flex;
`;

const filtersBadgeStyle = css`
    position: absolute;
    top: -${calculateRemSize(0.5)};
    right: -${calculateRemSize(0.5)};
    padding: 0.25rem 0.5rem;
    min-height: ${calculateRemSize(2)};
    font-size: ${calculateRemSize(2)};
    line-height: 1;
`;
